import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { BellIcon } from '@heroicons/react/24/outline';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import { VITE_PAST_DUE_LADDER_FEATURE } from 'src/constants/envs';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';
import { META_PRICING_ALERT_IDS } from 'src/alerts/MetaPricingNotice.types';
import {
  ALERT_CENTER_EVENTS,
  type tAlertDetailOpenedProps,
} from 'src/hooks/analytics/alertCenterAnalytics.constants';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { renderableNotifications } from 'src/notifications/AgendaNotifications.functions';
import { useAlertCenterContext } from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import { useNotificationsContext } from 'src/providers/NotificationProvider/Notifications.constants';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import AlertDetailModal from './AlertDetailModal.component';
import AlertPanel from './AlertPanel.component';
import {
  bellCount,
  bellSignalTone,
  permanentBellAlerts,
} from './AlertPanel.functions';
import type { tAlertBellProps } from './AlertPanel.types';
import './AlertBell.styles.scss';

/**
 * The Alert Center bell.
 *
 * Owns the panel's focus contract — moving focus in is the panel's job (it has
 * the container), returning it is this component's, because by the time the
 * panel is closed it no longer exists to return anything.
 *
 * Open state is lifted to the navbar so any other affordance can open the
 * same panel this button does; two independent panels would be two different
 * answers to "what else is there".
 */
export default function AlertBell(props: tAlertBellProps) {
  const { t } = useTranslation(ALERT_CENTER_NS);
  const context = useAlertCenterContext();
  const { currentNotifications } = useNotificationsContext();
  const { currentUserRole } = useUserContext();
  const { trackEvent } = useAnalytics();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Mirrored so `openDetail` keeps a stable identity across renders: the panel
  // feeds it straight into `useAlertCta`'s dependency list.
  const onOpenChangeRef = useRef(props.onOpenChange);
  onOpenChangeRef.current = props.onOpenChange;

  // U4 — the open detail, owned here (not by the panel) so it survives the
  // panel closing underneath it and any future surface can open the same
  // modal.
  const [detailAlert, setDetailAlert] = useState<tLiveAlert | null>(null);

  /**
   * RET-1957 (U3) — one surface at a time: opening a detail CLOSES the panel,
   * from the row and from the row's CTA alike. Two stacked popovers over a
   * dimmed board was the "everything at once" shape Ariel's rule retires, and
   * the panel is a glance, not a place to come back to.
   *
   * The invoker the panel hands over is therefore ignored HERE (other
   * surfaces still use it): by close time the row that opened the detail has
   * unmounted with the panel, so the bell is the only element left to return
   * focus to — and it is the right one, because that is where the user goes
   * to re-open the list.
   */
  /*
   * AGI-428: `front.alerts.detail.opened` shipped as a constant with RET-1875
   * and nothing ever emitted it, so the modal had no open rate at all. It is
   * emitted here rather than inside the modal because the ORIGIN is what the
   * event is for, and only the opener knows it: this is the deliberate path
   * ('bell'), the automatic host is the other one ('auto'), and telling them
   * apart is the whole measurement for this notice.
   *
   * The notice's own dimensions ride along when the alert is the Meta-pricing
   * family, so a bell open and an automatic open are comparable row by row.
   * `metaPricingNotice` is read through a ref: it changes on every focus
   * refetch, and this callback's identity feeds the panel's `useAlertCta`
   * dependency list.
   */
  const metaPricingNoticeRef = useRef(context.metaPricingNotice);
  metaPricingNoticeRef.current = context.metaPricingNotice;
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;

  const openDetail = useCallback((alert: tLiveAlert) => {
    setDetailAlert(alert);
    onOpenChangeRef.current(false);
    const payload =
      META_PRICING_ALERT_IDS.includes(alert.id)
        ? (metaPricingNoticeRef.current?.payload ?? null)
        : null;
    const openedProps: tAlertDetailOpenedProps = {
      alert_id: alert.id,
      from: 'bell',
      source: 'bell',
      ...(payload
        ? {
            segment: payload.segment,
            urgency: payload.urgency,
            key_account: payload.keyAccount,
            cta_url: payload.ctaUrl,
          }
        : {}),
    };
    trackEventRef.current(ALERT_CENTER_EVENTS.DETAIL_OPENED, openedProps);
  }, []);

  // Closing the detail does NOT reopen the panel: the user asked for this one
  // alert, and re-materialising the list behind them would be the second
  // surface again, one interaction later.
  const closeDetail = useCallback(() => {
    setDetailAlert(null);
    buttonRef.current?.focus();
  }, []);

  // D12 — the number counts only what the user can clear (pending
  // acknowledgeable P1s + undismissed P3s, G15 keeps contextual out).
  const alertCount = bellCount({
    alerts: context.alerts,
    inlineAlertIds: context.inlineAlertIds,
    role: currentUserRole ?? null,
  });

  /**
   * AGI-346 — the tray joins the same number. It satisfies D12's rule on its
   * own terms: every one of these IS clearable by the user (each row has its
   * dismiss), and none is claimed by an inline surface, so there is no
   * demotion to apply.
   *
   * Counted from `renderableNotifications`, not the raw provider list: a
   * notification the panel drops for an unrecognised event would otherwise
   * leave a number the user cannot empty by opening the panel.
   */
  const notifications = useMemo(
    () => renderableNotifications(currentNotifications),
    [currentNotifications],
  );
  const pending = alertCount + notifications.length;

  /*
   * D12's other half — alerts the user CANNOT clear do not inflate the number
   * (nothing they do empties it); the bell takes a visual state instead, and
   * returns to normal the moment the last of them resolves.
   *
   * 🔴 U12 (KTD7) widened this in two ways, and both were holes rather than
   * preferences:
   *
   *  - WHAT counts. It used to read `waitingCriticalAlerts.length > 0` — P0s
   *    that lost the wide arbitration, nothing else. A non-dismissible P1 or P3
   *    contributes zero to the badge precisely because it cannot be
   *    acknowledged, so the past-due ladder's first nine days (76% of
   *    recoveries) would have lit nothing at all.
   *  - WHEN the dot renders. It was `&& pending === 0`, so any owner holding a
   *    single clearable alert or one agenda notification saw no dot whatsoever.
   *    Since a non-dismissible alert can never reach the number, the dot is the
   *    ladder's ONLY bell signal — suppressing it behind a non-zero badge made
   *    that signal disappear in exactly the populated workspaces that have one.
   *    It now renders independently and steps out of the badge's corner.
   */
  const permanentAlerts = useMemo(
    () =>
      permanentBellAlerts({
        alerts: context.alerts,
        waitingCriticalAlerts: context.waitingCriticalAlerts,
        inlineAlertIds: context.inlineAlertIds,
        role: currentUserRole ?? null,
        isLadderEnabled: VITE_PAST_DUE_LADDER_FEATURE,
      }),
    [
      context.alerts,
      context.waitingCriticalAlerts,
      context.inlineAlertIds,
      currentUserRole,
    ],
  );
  const bellTone = bellSignalTone(permanentAlerts);
  const hasPermanentAttention = bellTone !== null;
  /*
   * KTD10 — with the flag OFF the dot keeps `staging`'s suppression, so this
   * component is unchanged for every user until the flag flips. The suppression
   * is the half that cannot live in `permanentBellAlerts`: `pending` is the
   * badge's number, which the bell owns.
   */
  const showAttentionDot =
    hasPermanentAttention && (VITE_PAST_DUE_LADDER_FEATURE || pending === 0);
  // Kept as its own name because it still means what it always meant — a live
  // P0 — and the accessible name below says "criticos en espera" only for that.
  const hasWaitingCritical = bellTone === 'critical';

  /**
   * AGI-346 — the arrival signal that replaces the toast. ONE run, ~1s, and it
   * is the only motion left: the toasts carried `animate-bounce`, which
   * Tailwind loops forever, so three stacked notifications never stopped
   * moving until the user destroyed them.
   *
   * Keyed on IDS, not on the count: a dismiss-then-arrive lands on the same
   * number and would go unannounced. The first run is skipped deliberately —
   * the list arrives from the load fetch, and everything in the inbox is not
   * "something just happened".
   */
  const [hasPulse, setHasPulse] = useState(false);
  const knownNotificationIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = new Set(notifications.map((notification) => notification.id));
    const known = knownNotificationIdsRef.current;
    knownNotificationIdsRef.current = ids;
    if (known === null) return undefined;
    const hasArrival = [...ids].some((id) => !known.has(id));
    if (!hasArrival) return undefined;
    setHasPulse(true);
    const timer = window.setTimeout(() => setHasPulse(false), 1100);
    return () => window.clearTimeout(timer);
  }, [notifications]);

  // Escape (or the close button) must land the user back where they started.
  // Guarded on "was open" so the first render never steals focus from whatever
  // the user is actually doing, and on "no detail open" (U3) because a panel
  // that closed to make room for a detail must not yank focus out of the modal
  // that just took it: the modal's trap runs first (child effects before
  // parent), so without this the bell would win the race every time.
  const isDetailOpen = detailAlert !== null;
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !props.isOpen && !isDetailOpen) {
      buttonRef.current?.focus();
    }
    wasOpenRef.current = props.isOpen;
  }, [props.isOpen, isDetailOpen]);

  // The panel is a popover: it traps Tab but draws no overlay, so a pointer
  // can still reach the page behind it. Without this, clicking away leaves the
  // panel open over a page the user has already moved on to.
  // Bound only while open, so a closed bell costs nothing — and since U3 the
  // panel is never open under a detail, so there is no suspension to arrange.
  const { isOpen, onOpenChange } = props;
  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen, onOpenChange]);

  /**
   * The count lives in the accessible NAME, recomputed whenever it changes.
   * A static label ("Avisos") would override the rendered badge for a screen
   * reader — the badge is `aria-hidden` precisely because this string is the
   * one that gets read, and a name that never changes would report a stale
   * count forever. The critical state is part of the same name: the dot is
   * visual-only, so without these variants a screen reader would hear a
   * calm "no pending" while the bell burns red.
   */
  /*
   * Three states, not two (U12). A yellow dot for an unpayable invoice is not
   * "criticos en espera", and saying so would be the same class of untruth the
   * ladder exists to remove — so a non-critical permanent alert gets its own
   * wording rather than borrowing the critical one or, worse, going unsaid.
   */
  const labelScope = hasWaitingCritical
    ? 'Critical'
    : hasPermanentAttention
      ? 'Attention'
      : '';
  const label =
    pending > 0
      ? t(`panel.bellLabel${labelScope}`, { count: pending })
      : t(`panel.bellLabel${labelScope}Empty`);

  return (
    <div ref={rootRef} className="AlertBell">
      <button
        ref={buttonRef}
        type="button"
        className={classNames('AlertBell__Button btn btn-ghost btn-sm btn-circle', {
          'AlertBell__Button--critical': hasWaitingCritical,
          'AlertBell__Button--pulse': hasPulse,
        })}
        data-testid="alert-bell"
        data-count={pending}
        data-pulse={hasPulse || undefined}
        data-critical={hasWaitingCritical || undefined}
        // The ramp's hue, read by the stylesheet. `--critical` above stays a
        // class because the red state predates the ramp and other code asserts
        // on it; the three softer tones are attribute-driven so adding a fourth
        // never costs another modifier class.
        data-bell-tone={bellTone ?? undefined}
        aria-expanded={props.isOpen}
        aria-haspopup="dialog"
        aria-label={label}
        onClick={() => props.onOpenChange(!props.isOpen)}
      >
        <BellIcon className="AlertBell__Icon" aria-hidden="true" />
        {pending > 0 && (
          <span
            className="AlertBell__Badge"
            data-testid="alert-bell-badge"
            aria-hidden="true"
          >
            {pending}
          </span>
        )}
        {showAttentionDot && (
          <span
            className={classNames('AlertBell__CriticalDot', {
              // The badge owns the top-right anchor whenever it exists, so the
              // dot moves rather than hides — the two now coexist.
              'AlertBell__CriticalDot--withBadge': pending > 0,
            })}
            data-testid="alert-bell-critical-dot"
            data-tone={bellTone ?? undefined}
            aria-hidden="true"
          />
        )}
      </button>

      {/*
        MOUNTED conditionally, never hidden with CSS. A hidden-but-mounted
        panel still runs its hooks; INT-834 is the repo's five-day workspace
        freeze caused by exactly that shape.
      */}
      {props.isOpen && (
        <AlertPanel
          onClose={() => props.onOpenChange(false)}
          onOpenDetail={openDetail}
        />
      )}

      {/*
        Rendered OUTSIDE the panel conditional on purpose: the panel closes the
        moment a detail opens (U3), and the detail must survive that. It also
        portals to the body, so it is not this root's subtree either way.
        Conditionally mounted, same KTD14 rule as the panel.
      */}
      {detailAlert && (
        <AlertDetailModal alert={detailAlert} onClose={closeDetail} />
      )}
    </div>
  );
}
